// Date helpers for calendar surfaces. Everything works on ISO business dates ("2026-03-20") in UTC, so a date never
// shifts with the viewer's time zone. No calendar library: the browser's Intl formats the words.

const ISO = /^\d{4}-\d{2}-\d{2}$/;

export function isIsoDate(value) {
  if (typeof value !== "string" || !ISO.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

const toDate = (iso) => new Date(`${iso}T00:00:00Z`);
const format = (iso, options) => (isIsoDate(iso) ? toDate(iso).toLocaleDateString("en-PH", { timeZone: "UTC", ...options }) : String(iso || ""));

export function addDays(iso, days) {
  const date = toDate(iso);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** "2026-03" */
export const monthOf = (iso) => String(iso).slice(0, 7);
export const yearOf = (iso) => Number(String(iso).slice(0, 4));
/** "March" (or "March 2026" with the year). */
export const monthName = (monthKey, { year = false } = {}) => format(`${monthKey}-01`, { month: "long", year: year ? "numeric" : undefined });
/** "Fri" */
export const weekdayShort = (iso) => format(iso, { weekday: "short" });
/** "20" */
export const dayOfMonth = (iso) => String(Number(String(iso).slice(8, 10)));
// Each date is put together from single words (weekday, month) so it reads day-month-year in every browser, whatever
// order that browser's locale data prefers.
/** "Friday 20 March" */
export function longDay(iso) {
  if (!isIsoDate(iso)) return String(iso || "");
  return `${format(iso, { weekday: "long" })} ${dayOfMonth(iso)} ${format(iso, { month: "long" })}`;
}
/** "Friday 20 March 2026" */
export const fullDate = (iso) => (isIsoDate(iso) ? `${longDay(iso)} ${yearOf(iso)}` : String(iso || ""));
/** "20 Mar 2026" (or "20 Mar" without the year). */
export function shortDate(iso, { year = true } = {}) {
  if (!isIsoDate(iso)) return String(iso || "");
  return `${dayOfMonth(iso)} ${format(iso, { month: "short" })}${year ? ` ${yearOf(iso)}` : ""}`;
}
/** "Fri 20 Mar" */
export const shortDay = (iso) => (isIsoDate(iso) ? `${weekdayShort(iso)} ${shortDate(iso, { year: false })}` : String(iso || ""));

/** Entries in date order, grouped by month: [{ month: "2026-03", entries: [...] }]. Order within a date is kept. */
export function groupByMonth(entries) {
  const sorted = entries.map((entry, index) => ({ entry, index }))
    .sort((a, b) => (a.entry.date === b.entry.date ? a.index - b.index : a.entry.date < b.entry.date ? -1 : 1));
  const groups = [];
  for (const { entry } of sorted) {
    const month = monthOf(entry.date);
    const last = groups[groups.length - 1];
    if (last && last.month === month) last.entries.push(entry);
    else groups.push({ month, entries: [entry] });
  }
  return groups;
}
